import { Fragment, ReactNode, useContext, useMemo } from "react";
import { ModuleInfo, ComponentSubscription } from "../model";
import { ShellContext } from "../context";
import { useOwnerModule } from "../hooks";

/**
 * Returns the components subscribed to the content channel with the specified ID.
 * @param id The content channel ID.
 * @returns An array of content subscriptions.
 */
export function useContentChannel(id: string): ReadonlyArray<ComponentSubscription>;

/**
 * Returns the components subscribed to the content channel with the module and channel ID.
 * @param moduleId The module ID to get channels for.
 * @param channelId The content channel ID.
 * @returns An array of content subscriptions.
 */
export function useContentChannel(moduleId: string | null, channelId: string): ReadonlyArray<ComponentSubscription>;

/**
 * Returns the components subscribed to the content channel with the specified ID.
 * @param id The content channel ID.
 * @returns An array of content subscriptions.
 */
export function useContentChannel(...args: [string | null, string?]): ReadonlyArray<ComponentSubscription> {
  let moduleId: string | null;
  let channelId: string;

  const ownerModule = useOwnerModule();

  if (args.length === 1) {
    moduleId = ownerModule ? ownerModule.id : null;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    channelId = args[0]!;
  } else {
    moduleId = args[0];

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    channelId = args[1]!;
  }

  const { getChannelSubscriptions } = useContext(ShellContext);

  const channelSubscriptions = useMemo(() => {
    return getChannelSubscriptions(moduleId, channelId).filter(
      (subscription) => subscription.type === "component",
    ) as ReadonlyArray<ComponentSubscription>;
  }, [getChannelSubscriptions, moduleId, channelId]);

  return channelSubscriptions;
}

/** The `ContentChannel` component properties. */
export interface ContentChannelProps {
  
  /** The channel ID. */
  readonly id: string;

  /** The template to use to render each child component. */
  readonly children?: (child: JSX.Element, index: number, count: number, module: ModuleInfo, params?: unknown) => JSX.Element;
}

/** Creates a channel through which other modules are able to publish components. */
export const ContentChannel = (props: ContentChannelProps): ReactNode => {
  const { id, children } = props;

  const channelSubscriptions = useContentChannel(id);
  const length = channelSubscriptions.length;

  return (
    <>
      {channelSubscriptions.map((subscription, index) => {
        if (subscription.type !== "component") return null;

        const { module, component: Component, params } = subscription;

        const child = children ? children(<Component />, index, length, module, params) : <Component />;
        if (child === null) return null;

        return <Fragment key={index}>{child}</Fragment>;
      })}
    </>
  );
};
